/* Web Push client helpers. Uses the browser Push API + our /api/push/* endpoints. */
import http from "@/api";

// base64url → Uint8Array (for applicationServerKey)
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window;
}

export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    // v7 — auto-update: tell SW to skip waiting + activate immediately so a
    // freshly-bumped cache version takes effect without requiring the user
    // to fully close the PWA. (was the cause of "I deleted everything but I
    // still see products" reports.)
    if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
    reg.addEventListener("updatefound", () => {
      const next = reg.installing;
      if (!next) return;
      next.addEventListener("statechange", () => {
        if (next.state === "installed" && navigator.serviceWorker.controller) {
          // A new SW is waiting → tell it to activate now
          reg.waiting?.postMessage({ type: "SKIP_WAITING" });
        }
      });
    });
    return reg;
  } catch (e) { console.warn("SW register failed:", e); return null; }
}

/** Force-clear all browser caches + unregister the SW. Use when stale data
 *  is being served from PWA cache (e.g. after wiping the catalog). */
export async function flushAllCaches() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    return { ok: true };
  } catch (e) {
    console.warn("flushAllCaches failed:", e);
    return { ok: false, error: String(e) };
  }
}

export async function pushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;  // "default" | "granted" | "denied"
}

export async function subscribePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };

  const { data: vapid } = await http.get("/push/vapid-key");
  if (!vapid?.enabled || !vapid.public_key) return { ok: false, reason: "server_disabled" };

  const reg = await registerSW();
  if (!reg) return { ok: false, reason: "no_sw" };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
    });
  }
  const json = sub.toJSON();
  await http.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys || {},
    ua: navigator.userAgent,
  });
  return { ok: true };
}

export async function unsubscribePush() {
  if (!pushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration("/");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await http.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`); }
    catch (e) { console.error("[push] backend unsubscribe failed (will still unsubscribe locally):", e?.message || e); }
    await sub.unsubscribe();
  }
  return { ok: true };
}
